import { DocumentReference, Timestamp } from '@google-cloud/firestore';
import { TaxRate } from '@principle-theorem/accounting';
import {
  Appointment,
  HealthcareClaimGenerator,
  Invoice,
  Practice,
  stafferToNamedDoc,
  toAccountDetails,
  TransactionAllocation,
  treatmentStepToLineItems,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  IInvoice,
  IPatient,
  isPatientWithPrimaryContact,
  ITreatmentStep,
  SpecialTransactionAllocationTarget,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  isDocRef,
  toNamedDocument,
  WithRef,
} from '@principle-theorem/shared';

export async function buildInvoiceForAppointment(
  appointment: WithRef<IAppointment>,
  patientRef: DocumentReference<IPatient>,
  taxRate: TaxRate,
  createdAt?: Timestamp
): Promise<IInvoice> {
  if (!appointment.event) {
    throw new Error('Missing required data');
  }

  const practice = await Firestore.getDoc(appointment.event.practice.ref);
  const patient = await Firestore.getDoc(patientRef);
  const primaryContact = isPatientWithPrimaryContact(patient)
    ? await Firestore.getDoc(patient.primaryContact.patient.ref)
    : undefined;

  const treatmentPlan = await Appointment.treatmentPlan(appointment);
  const practitioner = await Firestore.getDoc(appointment.practitioner.ref);

  const treatmentStep = await Firestore.getDoc<ITreatmentStep>(
    appointment.treatmentPlan.treatmentStep.ref
  );

  const items = treatmentStep
    ? treatmentStepToLineItems(
        treatmentStep,
        treatmentPlan,
        stafferToNamedDoc(practitioner),
        taxRate
      )
    : [];

  const claims = await HealthcareClaimGenerator.createClaims(
    items,
    practitioner,
    practice.ref
  );

  const invoice = Invoice.init({
    from: toAccountDetails(practice),
    to: primaryContact
      ? toAccountDetails({ patient, primaryContact })
      : toAccountDetails(patient),
    practice: toNamedDocument(practice),
    items,
    claims,
    createdAt,
  });

  const brand = await Firestore.getDoc(Practice.brandRef(practice));
  if (!brand.settings.billing?.enableBillingEntitySelection) {
    return invoice;
  }

  const billingEntity = await getBillingEntityForInvoice(invoice);
  if (!billingEntity) {
    return invoice;
  }

  return {
    ...invoice,
    from: billingEntity.from,
    attributedTo: billingEntity.attributedTo,
  };
}

async function getBillingEntityForInvoice(
  invoice: IInvoice
): Promise<Pick<IInvoice, 'from' | 'attributedTo'> | undefined> {
  const attributedTo =
    TransactionAllocation.getPrimaryAllocationTarget(invoice);

  if (
    !attributedTo ||
    attributedTo === SpecialTransactionAllocationTarget.Unallocated
  ) {
    const practice = await Firestore.getDoc(invoice.practice.ref);
    const billingEntity = practice.billingEntities?.find(
      (entity) => entity.isDefault
    );

    if (!billingEntity) {
      return;
    }

    return {
      from: toAccountDetails({ practice, billingEntity }),
      attributedTo: {
        ref: practice.ref,
        billingEntityUid: billingEntity.uid,
      },
    };
  }

  if (isDocRef(attributedTo)) {
    const attributedToPractitioner = await Firestore.getDoc(attributedTo);
    const billingEntity = attributedToPractitioner.billingEntities?.find(
      (entity) => entity.isDefault
    );

    if (!billingEntity) {
      return;
    }

    return {
      from: toAccountDetails({
        staffer: attributedToPractitioner,
        billingEntity,
      }),
      attributedTo: {
        ref: attributedToPractitioner.ref,
        billingEntityUid: billingEntity.uid,
      },
    };
  }
}
